import React, { useState, useEffect, useCallback } from 'react';
import { expenseAPI } from '../services/api';
import { FiPlus, FiEdit2, FiTrash2, FiX } from 'react-icons/fi';

/** שמות החודשים בעברית */
const HEBREW_MONTHS = [
  'ינואר',
  'פברואר',
  'מרץ',
  'אפריל',
  'מאי',
  'יוני',
  'יולי',
  'אוגוסט',
  'ספטמבר',
  'אוקטובר',
  'נובמבר',
  'דצמבר',
];

/**
 * Expenses - דף ניהול הוצאות
 * מציג רשימת הוצאות עם אפשרויות סינון, הוספה, עריכה ומחיקה
 */
const Expenses = () => {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [formData, setFormData] = useState({
    amount: '',
    category: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
  });
  const [error, setError] = useState('');
  const [categories, setCategories] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  /**
   * טעינת קטגוריות הוצאה מהשרת
   */
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await expenseAPI.getCategories();
        setCategories(response.data.categories || []);
      } catch (err) {
        console.error('שגיאה בטעינת קטגוריות:', err);
      }
    };
    fetchCategories();
  }, []);

  /**
   * טעינת הוצאות מהשרת לפי חודש ושנה שנבחרו
   */
  const fetchExpenses = useCallback(async () => {
    setLoading(true);
    try {
      const response = await expenseAPI.getAll({
        month: selectedMonth,
        year: selectedYear,
      });
      setExpenses(response.data.expenses || []);
    } catch (err) {
      console.error('שגיאה בטעינת הוצאות:', err);
      setExpenses([]);
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, selectedYear]);

  /** טעינה ראשונית ובכל שינוי חודש/שנה */
  useEffect(() => {
    fetchExpenses();
  }, [fetchExpenses]);

  /**
   * עיצוב מספר למטבע ישראלי
   * @param {number} value - הערך המספרי
   * @returns {string} מחרוזת מעוצבת עם סימן שקל
   */
  const formatCurrency = (value) => {
    if (value == null) return '₪0';
    return `₪${Number(value).toLocaleString('he-IL')}`;
  };

  /**
   * עיצוב תאריך לתצוגה בעברית
   * @param {string} dateStr - מחרוזת תאריך
   * @returns {string} תאריך מעוצב
   */
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('he-IL');
  };

  /**
   * חישוב סכום כולל של כל ההוצאות המוצגות
   * @returns {number} סכום כולל
   */
  const totalAmount = expenses.reduce(
    (sum, expense) => sum + Number(expense.amount || 0),
    0
  );

  /**
   * פתיחת חלון הוספת הוצאה חדשה
   */
  const openAddModal = () => {
    setEditingExpense(null);
    setFormData({
      amount: '',
      category: '',
      description: '',
      date: new Date().toISOString().split('T')[0],
    });
    setError('');
    setShowModal(true);
  };

  /**
   * פתיחת חלון עריכת הוצאה קיימת
   * @param {Object} expense - אובייקט ההוצאה לעריכה
   */
  const openEditModal = (expense) => {
    setEditingExpense(expense);
    setFormData({
      amount: expense.amount || '',
      category: expense.category || '',
      description: expense.description || '',
      date: expense.date
        ? new Date(expense.date).toISOString().split('T')[0]
        : new Date().toISOString().split('T')[0],
    });
    setError('');
    setShowModal(true);
  };

  /**
   * סגירת חלון ההוספה/עריכה
   */
  const closeModal = () => {
    setShowModal(false);
    setEditingExpense(null);
    setError('');
  };

  /**
   * עדכון שדה בטופס
   * @param {Event} e - אירוע שינוי בשדה
   */
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  /**
   * שמירת הוצאה - הוספה חדשה או עדכון קיימת
   * @param {Event} e - אירוע שליחת הטופס
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const payload = { ...formData, amount: Number(formData.amount) };
      if (editingExpense) {
        await expenseAPI.update(editingExpense._id || editingExpense.id, payload);
      } else {
        await expenseAPI.create(payload);
      }
      closeModal();
      fetchExpenses();
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          'שגיאה בשמירת ההוצאה'
      );
    }
  };

  /**
   * מחיקת הוצאה לאחר אישור מהמשתמש
   * @param {string} id - מזהה ההוצאה למחיקה
   */
  const handleDelete = async (id) => {
    if (!window.confirm('האם למחוק הוצאה זו?')) return;

    try {
      await expenseAPI.delete(id);
      fetchExpenses();
    } catch (err) {
      console.error('שגיאה במחיקת הוצאה:', err);
    }
  };

  /**
   * תצוגת טעינה - ספינר
   */
  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>טוען נתונים...</p>
      </div>
    );
  }

  return (
    <div className="page">
      {/* כותרת הדף */}
      <div className="page-header">
        <h1>הוצאות</h1>
        <p className="page-subtitle">ניהול ההוצאות שלך</p>
      </div>

      {/* סרגל סינון - בחירת חודש, שנה וכפתור הוספה */}
      <div className="filter-bar">
        <select
          className="form-control"
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(Number(e.target.value))}
        >
          {HEBREW_MONTHS.map((month, index) => (
            <option key={index + 1} value={index + 1}>
              {month}
            </option>
          ))}
        </select>
        <select
          className="form-control"
          value={selectedYear}
          onChange={(e) => setSelectedYear(Number(e.target.value))}
        >
          {Array.from({ length: 11 }, (_, i) => 2020 + i).map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </select>
        <button className="btn btn-success" onClick={openAddModal}>
          <FiPlus /> הוסף הוצאה
        </button>
      </div>

      {/* שורת סיכום - סה"כ הוצאות */}
      <div className="summary-line">
        <strong>סה"כ הוצאות: {formatCurrency(totalAmount)}</strong>
      </div>

      {/* תצוגת הוצאות - טבלה לדסקטופ וכרטיסים למובייל */}
      {expenses.length === 0 ? (
        <div className="empty-state">
          <p>אין הוצאות להצגה עבור התקופה שנבחרה</p>
        </div>
      ) : (
        <>
          {/* טבלת הוצאות - דסקטופ */}
          <div className="desktop-only">
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>תאריך</th>
                    <th>קטגוריה</th>
                    <th>תיאור</th>
                    <th>סכום</th>
                    <th>פעולות</th>
                  </tr>
                </thead>
                <tbody>
                  {expenses.map((expense) => (
                    <tr key={expense._id || expense.id}>
                      <td>{formatDate(expense.date)}</td>
                      <td>
                        <span className="badge badge-info">
                          {expense.category}
                        </span>
                      </td>
                      <td>{expense.description}</td>
                      <td>{formatCurrency(expense.amount)}</td>
                      <td>
                        <button
                          className="btn btn-sm btn-outline"
                          onClick={() => openEditModal(expense)}
                          title="עריכה"
                        >
                          <FiEdit2 />
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() =>
                            handleDelete(expense._id || expense.id)
                          }
                          title="מחיקה"
                        >
                          <FiTrash2 />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* רשימת כרטיסים - מובייל */}
          <div className="mobile-only">
            <div className="mobile-list">
              {expenses.map((expense) => (
                <div className="mobile-card" key={expense._id || expense.id}>
                  <div className="mobile-card-row">
                    <span className="badge badge-info">{expense.category}</span>
                    <span className="mobile-card-date">{formatDate(expense.date)}</span>
                  </div>
                  {expense.description && <p className="mobile-card-desc">{expense.description}</p>}
                  <div className="mobile-card-row">
                    <span className="mobile-card-amount expense">{formatCurrency(expense.amount)}</span>
                    <div className="mobile-card-actions">
                      <button className="btn btn-sm btn-outline" onClick={() => openEditModal(expense)} title="עריכה"><FiEdit2 /></button>
                      <button className="btn btn-sm btn-danger" onClick={() => handleDelete(expense._id || expense.id)} title="מחיקה"><FiTrash2 /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* חלון מודאלי להוספה/עריכה */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            {/* כותרת המודאל */}
            <div className="modal-header">
              <h2>
                {editingExpense ? 'עריכת הוצאה' : 'הוספת הוצאה'}
              </h2>
              <button className="btn btn-sm btn-outline" onClick={closeModal}>
                <FiX />
              </button>
            </div>

            {/* גוף המודאל - טופס */}
            <div className="modal-body">
              {error && <div className="alert-error">{error}</div>}

              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="amount">סכום</label>
                  <input
                    id="amount"
                    type="number"
                    name="amount"
                    className="form-control"
                    value={formData.amount}
                    onChange={handleFormChange}
                    placeholder="הזן סכום"
                    required
                    min="0"
                    step="0.01"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="category">קטגוריה</label>
                  <select
                    id="category"
                    name="category"
                    className="form-control"
                    value={formData.category}
                    onChange={handleFormChange}
                    required
                  >
                    <option value="">בחר קטגוריה</option>
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="description">תיאור</label>
                  <input
                    id="description"
                    type="text"
                    name="description"
                    className="form-control"
                    value={formData.description}
                    onChange={handleFormChange}
                    placeholder="הזן תיאור"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="date">תאריך</label>
                  <input
                    id="date"
                    type="date"
                    name="date"
                    className="form-control"
                    value={formData.date}
                    onChange={handleFormChange}
                    required
                  />
                </div>

                {/* כפתורי פעולה */}
                <div className="modal-footer">
                  <button type="submit" className="btn btn-primary">
                    {editingExpense ? 'עדכון' : 'שמירה'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={closeModal}
                  >
                    ביטול
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Expenses;
